<script setup>
/* eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) */
/**
 * CrmConversationKanban — bloco CRM dentro da conversa (F4 / Seam A).
 *
 * Seção compacta no ContactPanel, ACIMA do Draggable, render condicional a isCrmLinked.
 * Edita o deal vinculado à conversa sem sair do atendimento. SEM botões terminais
 * (ganho/perda vivem no ResolveAction — Seam B); aqui só o trabalho do dia-a-dia:
 *   • Funil — read-only.
 *   • Etapa — pill + setas ◀▶ (moveDeal otimista com rollback no erro).
 *   • Agentes — seletor que grava agente_id (updateDeal).
 *   • Prioridade — 3 chips (alta / neutra / baixa).
 *   • Etiquetas — labels NATIVAS da conversa (reusa useConversationLabels — sem taxonomia nova).
 *   • Data de início (inicio_em) + Data de vencimento (vence_em).
 *
 * Estado lido/gravado via useCrmConversationDeal (cache module-scoped, compartilhado
 * com o ResolveAction). White-mode: tudo dentro de .crm-light-scope.
 */
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useCrmConversationDeal } from 'dashboard/composables/cw_custom/useCrmConversationDeal.js';
import crmBoardApi from 'dashboard/api/cw_custom/crmBoard.js';
import CrmMultiSelect from './CrmMultiSelect.vue';
import CrmProximaAcao from './CrmProximaAcao.vue';
import CrmTimeline from './CrmTimeline.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { formatDataDdMmAaaa } from './composables/useMetasFormat.js';
import { useMetaWindow } from './composables/useMetaWindow.js';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  accountId: { type: [Number, String], required: true },
});

const { deal, stages, agents, actionTypes, refresh, applyDeal } =
  useCrmConversationDeal(
    () => props.accountId,
    () => props.conversationId
  );

const { isAdmin } = useAdmin();

// A JANELA DA META (2026-08-06) — aberta / fecha em Xh / fechada. Fora do board não há tick
// compartilhado, então o composable usa o timer local dele. Ver Crm::MetaWindow.
const {
  mostrar: janelaMostrar,
  fechada: janelaFechada,
  label: janelaLabel,
  titulo: janelaTitulo,
} = useMetaWindow(() => deal.value?.janela);

// Etapas ordenadas (o backend já manda non-terminal por posicao asc).
const orderedStages = computed(() =>
  [...(stages.value ?? [])].sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0))
);

const currentStageIndex = computed(() =>
  orderedStages.value.findIndex(s => s.id === deal.value?.stage_id)
);

const currentStage = computed(
  () => orderedStages.value[currentStageIndex.value] ?? null
);

// Pill da etapa — neutra slate para todas as etapas (board 100% MONOCROMÁTICO /
// INK-ONLY, disciplina do amarelo). Em sincronia com CrmColumn.COLOR_CLASSES.
const stagePillClass = 'bg-n-slate-3 text-n-slate-11';

// ◀ desabilita na 1ª etapa; ▶ na última não-terminal. -1 (etapa fora da lista,
// ex.: deal já fechado mostrando etapa de trabalho) trava as duas setas.
const canPrev = computed(() => currentStageIndex.value > 0);
const canNext = computed(
  () =>
    currentStageIndex.value >= 0 &&
    currentStageIndex.value < orderedStages.value.length - 1
);

const movingStage = ref(false);

async function moveStage(direction) {
  if (movingStage.value) return;
  const idx = currentStageIndex.value;
  if (idx < 0) return;
  const targetIdx = idx + direction;
  if (targetIdx < 0 || targetIdx >= orderedStages.value.length) return;

  const targetStage = orderedStages.value[targetIdx];
  const prevDeal = deal.value;

  // Otimista: aplica a etapa-alvo já; reverte se o servidor recusar (ex.: 422 do guard).
  applyDeal({ ...prevDeal, stage_id: targetStage.id });
  movingStage.value = true;
  try {
    await crmBoardApi.moveDeal(props.accountId, prevDeal.id, targetStage.id);
    // O move_stage do board devolve um deal com SHAPE ENXUTO (sem funnel_nome,
    // sem datas, sem tasks). Aplicá-lo aqui clobberia o deal rico do by_conversation.
    // Refazemos o fetch para manter o estado completo e em sincronia (orientação do backend).
    await refresh();
  } catch (e) {
    applyDeal(prevDeal); // rollback
    const msg =
      e.response?.data?.error ??
      'Não foi possível mover a etapa. Tente novamente.';
    useAlert(msg);
  } finally {
    movingStage.value = false;
  }
}

// ── Persistência genérica de campos do deal (agente/prioridade/datas) ──
// Otimista com rollback; sincroniza o cache compartilhado via applyDeal.
const saving = ref(false);

async function persistDeal(patch) {
  if (!deal.value) return;
  const prevDeal = deal.value;
  applyDeal({ ...prevDeal, ...patch });
  saving.value = true;
  try {
    const res = await crmBoardApi.updateDeal(
      props.accountId,
      prevDeal.id,
      patch
    );
    applyDeal(res.data?.deal ?? res.data ?? { ...prevDeal, ...patch });
  } catch (e) {
    applyDeal(prevDeal); // rollback
    const msg =
      e.response?.data?.error ?? 'Não foi possível salvar. Tente novamente.';
    useAlert(msg);
  } finally {
    saving.value = false;
  }
}

// ── Agentes: CrmMultiSelect espera [{ id, label, icon? }] e v-model array de ids ──
const agentOptions = computed(() =>
  (agents.value ?? []).map(a => ({ id: a.id, label: a.name }))
);

// O deal guarda um único agente_id; o multiselect é multi por contrato, então
// modelamos como array de 0..1 e gravamos o primeiro (ou null) no backend.
const selectedAgentIds = computed({
  get() {
    return deal.value?.agente_id ? [deal.value.agente_id] : [];
  },
  set(ids) {
    // Mantém só o último selecionado (papel é responsável único).
    const next = ids.length ? ids[ids.length - 1] : null;
    persistDeal({ agente_id: next });
  },
});

// ── Prioridade: 3 chips. Slugs do enum do Deal (deal.rb): neutra | alta | baixa ──
const PRIORIDADES = [
  {
    value: 'alta',
    label: 'Alta',
    active: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
  },
  {
    value: 'neutra',
    label: 'Neutra',
    active: 'bg-n-slate-4 text-n-slate-12 border-n-slate-7',
  },
  {
    // custom (redesign Modeloja): "baixa" era azul — fora da marca. Prioridade baixa
    // é o estado CALMO: neutro slate mais leve que "neutra", reservando o ruby de
    // "alta" como o único sinal de atenção. Sem azul.
    value: 'baixa',
    label: 'Baixa',
    active: 'bg-n-slate-3 text-n-slate-11 border-n-slate-6',
  },
];

const currentPrioridade = computed(() => deal.value?.prioridade ?? 'neutra');

function setPrioridade(value) {
  if (value === currentPrioridade.value) return;
  persistDeal({ prioridade: value });
}

// ── Datas: <input type=date> grava YYYY-MM-DD; o backend aceita vence_em/inicio_em ──
function toDateInput(iso) {
  if (!iso) return '';
  // ISO datetime/date → YYYY-MM-DD (recorte simples, sem TZ shift do new Date()).
  return String(iso).slice(0, 10);
}

const inicioEm = computed({
  get: () => toDateInput(deal.value?.inicio_em),
  set: value => persistDeal({ inicio_em: value || null }),
});

const venceEm = computed({
  get: () => toDateInput(deal.value?.vence_em),
  set: value => persistDeal({ vence_em: value || null }),
});

// ── Etiquetas: labels nativas da conversa (sem taxonomia nova) ──
const {
  accountLabels,
  savedLabels,
  activeLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

const showLabelDropdown = ref(false);
const toggleLabelDropdown = () => {
  showLabelDropdown.value = !showLabelDropdown.value;
};
const closeLabelDropdown = () => {
  showLabelDropdown.value = false;
};

// Funil read-only — esconde o rótulo quando o backend não manda o nome.
const funnelNome = computed(() => deal.value?.funnel_nome ?? null);

// ── Estado terminal do deal (ganho/perdido) ──
// by_conversation devolve o deal FECHADO de propósito (o bloco segue visível pro
// contexto). Quando resultado !== 'aberto', a UI fica READ-ONLY: sem setas, sem
// chips graváveis, sem inputs editáveis — o backend ganha guard 422, mas a UI nem
// oferece. Etiquetas (conversa) também ficam read-only para consistência.
const isClosed = computed(
  () => !!deal.value && deal.value.resultado !== 'aberto'
);
const isWon = computed(() => deal.value?.resultado === 'ganho');

// Rótulos dos motivos de perda — espelham LOSS_REASONS do CrmLoseReasonPopover (spec 6.3).
// Inclui os slugs legados (timing, produto_nao_adequado) p/ render de dados históricos.
const LOSS_REASON_LABELS = {
  preco: 'Preço',
  concorrencia: 'Comprou concorrente',
  sem_resposta: 'Sumiu/sem resposta',
  frete_distancia: 'Frete/distância',
  prazo_longo: 'Prazo longo',
  nao_qualificado: 'Fora de perfil',
  outro: 'Outro',
  timing: 'Timing',
  produto_nao_adequado: 'Produto não adequado',
};

const motivoPerdaLabel = computed(() => {
  const m = deal.value?.motivo_perda;
  if (!m) return null;
  return LOSS_REASON_LABELS[m] ?? m;
});

const motivoPerdaDetalhe = computed(
  () => deal.value?.motivo_perda_detalhe ?? null
);

function fmtFechouEm(iso) {
  return formatDataDdMmAaaa(iso) || null;
}
const fechouEmFmt = computed(() => fmtFechouEm(deal.value?.fechou_em));

// Etiqueta de prioridade legível para o estado read-only.
const prioridadeLabel = computed(
  () =>
    PRIORIDADES.find(p => p.value === currentPrioridade.value)?.label ??
    'Neutra'
);

// Nome do responsável (read-only) — resolvido da lista de agents.
const agenteNome = computed(() => {
  const id = deal.value?.agente_id;
  if (!id) return null;
  return (agents.value ?? []).find(a => a.id === id)?.name ?? null;
});

// ── Timeline (F2): contexto secundário, colapsado por padrão ──
// O bloco na conversa já é denso; a timeline entra recolhida e o operador expande
// quando quer o histórico. Read-only — render delegado a CrmTimeline.
const showTimeline = ref(false);
const dealEvents = computed(() => deal.value?.deal_events ?? []);
const toggleTimeline = () => {
  showTimeline.value = !showTimeline.value;
};
</script>

<template>
  <!-- crm-light-scope: defesa-em-profundidade — o bloco vive na sidebar de conversa
       (tema global dark); a classe garante os tokens light dos componentes CRM. -->
  <section
    v-if="deal"
    class="crm-light-scope mx-2 mt-3 mb-1 rounded-card border border-n-weak bg-n-background overflow-visible"
    aria-label="CRM do lead"
  >
    <!-- Header: marca o bloco como CRM -->
    <div class="flex items-center gap-2 px-3 pt-3 pb-2">
      <span class="i-lucide-target size-3.5 text-n-slate-10 flex-shrink-0" />
      <span class="text-mj-13 font-500 text-n-slate-10"> CRM </span>
      <span v-if="funnelNome" class="text-mj-11 text-n-slate-10 truncate">
        · {{ funnelNome }}
      </span>

      <!-- A JANELA DA META (2026-08-06), aqui no lugar onde ela decide algo: o vendedor está
           OLHANDO a conversa e vai responder. Fechada, responder exige template pago — é a
           informação que muda a próxima ação dele, e é a razão de o indicador existir também
           aqui, e não só no board. -->
      <span
        v-if="janelaMostrar"
        class="ml-auto inline-flex items-center gap-1 text-mj-11 font-500 rounded-md px-1.5 py-px flex-shrink-0"
        :class="
          janelaFechada
            ? 'bg-n-ruby-3 text-n-ruby-11'
            : 'border border-n-weak text-n-slate-11'
        "
        :title="janelaTitulo"
      >
        <span
          class="size-3 flex-shrink-0"
          :class="
            janelaFechada
              ? 'i-lucide-message-square-off text-n-ruby-9'
              : 'i-lucide-message-square-dot text-n-slate-10'
          "
        />
        {{ janelaLabel }}
      </span>
    </div>

    <!-- ══════════════════════════════════════════════════
         DEAL FECHADO — read-only. Banner do desfecho + dados estáticos.
         Setas/chips/inputs/agente NÃO renderizam (backend rejeita c/ 422; a UI
         nem oferece). Mantém o bloco visível pro contexto histórico.
         ══════════════════════════════════════════════════ -->
    <div v-if="isClosed" class="px-3 pb-3 flex flex-col gap-3">
      <!-- Banner do desfecho -->
      <div
        class="px-3 py-2.5 rounded-lg text-mj-12 font-500 flex items-start gap-2"
        :class="[
          isWon
            ? 'text-mj-ganha-ink bg-mj-ganha-bg border border-[#86EFAC] dark:text-n-teal-11 dark:bg-n-teal-3 dark:border-n-teal-6'
            : 'text-n-slate-11 bg-n-slate-3 border border-n-slate-6',
        ]"
      >
        <span
          class="flex-shrink-0 mt-0.5"
          :class="
            isWon ? 'i-lucide-check-circle size-4' : 'i-lucide-x-circle size-4'
          "
        />
        <div class="flex flex-col gap-0.5 min-w-0">
          <span v-if="isWon">Ganho</span>
          <span v-else>
            Perdido{{ motivoPerdaLabel ? ' — ' + motivoPerdaLabel : '' }}
          </span>
          <span
            v-if="!isWon && motivoPerdaDetalhe"
            class="text-mj-11 font-400 text-n-slate-10 whitespace-pre-wrap break-words"
          >
            {{ motivoPerdaDetalhe }}
          </span>
          <span
            v-if="fechouEmFmt"
            class="text-mj-11 font-400 opacity-70 tabular-nums"
          >
            Fechado em {{ fechouEmFmt }}
          </span>
        </div>
      </div>

      <!-- Dados estáticos (read-only): etapa de trabalho, responsável, prioridade -->
      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-1">
          <span class="text-mj-12 font-500 text-n-slate-10"> Etapa </span>
          <span class="text-mj-12 font-500 text-n-slate-12">
            {{ currentStage?.nome ?? 'Sem etapa' }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-mj-12 font-500 text-n-slate-10"> Prioridade </span>
          <span class="text-mj-12 font-500 text-n-slate-12">
            {{ prioridadeLabel }}
          </span>
        </div>
        <div v-if="agenteNome" class="flex flex-col gap-1">
          <span class="text-mj-12 font-500 text-n-slate-10"> Responsável </span>
          <span class="text-mj-12 font-500 text-n-slate-12 truncate">
            {{ agenteNome }}
          </span>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════
         DEAL ABERTO — controles editáveis.
         ══════════════════════════════════════════════════ -->
    <div v-else class="px-3 pb-3 flex flex-col gap-3">
      <!-- ── PRÓXIMA AÇÃO — governança da F1; lidera o bloco (driver do dia-a-dia) ── -->
      <div class="flex flex-col gap-1.5">
        <span class="text-mj-12 font-500 text-n-slate-10"> Próxima ação </span>
        <CrmProximaAcao
          :account-id="accountId"
          :deal-id="deal.id"
          :action-types="actionTypes"
          :agents="agents"
          :default-agente-id="deal.agente_id"
          @changed="refresh"
        />
      </div>

      <!-- ── ETAPA — pill + setas ◀▶ ── -->
      <div class="flex flex-col gap-1.5">
        <span class="text-mj-12 font-500 text-n-slate-10"> Etapa </span>
        <div class="flex items-center gap-1.5">
          <button
            type="button"
            :disabled="!canPrev || movingStage"
            class="size-7 inline-flex items-center justify-center rounded-md border border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque disabled:opacity-40 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background flex-shrink-0"
            title="Etapa anterior"
            aria-label="Mover para a etapa anterior"
            @click="moveStage(-1)"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>

          <span
            class="flex-1 h-7 inline-flex items-center justify-center text-center text-mj-12 font-500 rounded-md px-2 truncate"
            :class="[stagePillClass]"
          >
            {{ currentStage?.nome ?? 'Sem etapa' }}
          </span>

          <button
            type="button"
            :disabled="!canNext || movingStage"
            class="size-7 inline-flex items-center justify-center rounded-md border border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque disabled:opacity-40 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background flex-shrink-0"
            title="Próxima etapa"
            aria-label="Mover para a próxima etapa"
            @click="moveStage(1)"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
        </div>
      </div>

      <!-- ── AGENTES — responsável ── -->
      <div class="flex flex-col gap-1.5">
        <span class="text-mj-12 font-500 text-n-slate-10"> Responsável </span>
        <CrmMultiSelect
          v-model="selectedAgentIds"
          :options="agentOptions"
          placeholder="Sem responsável"
          empty-text="Nenhum agente disponível."
        />
      </div>

      <!-- ── PRIORIDADE — 3 chips ── -->
      <div class="flex flex-col gap-1.5">
        <span class="text-mj-12 font-500 text-n-slate-10"> Prioridade </span>
        <div class="grid grid-cols-3 gap-1.5">
          <button
            v-for="p in PRIORIDADES"
            :key="p.value"
            type="button"
            class="h-7 inline-flex items-center justify-center text-mj-12 font-500 rounded-md border transition-colors duration-mj-toque focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
            :class="[
              currentPrioridade === p.value
                ? p.active
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1',
            ]"
            :aria-pressed="currentPrioridade === p.value"
            @click="setPrioridade(p.value)"
          >
            {{ p.label }}
          </button>
        </div>
      </div>

      <!-- ── ETIQUETAS — labels nativas da conversa ── -->
      <div class="flex flex-col gap-1.5">
        <span class="text-mj-12 font-500 text-n-slate-10"> Etiquetas </span>
        <div
          v-on-clickaway="closeLabelDropdown"
          class="relative flex flex-wrap items-center gap-1.5"
        >
          <span
            v-for="label in activeLabels"
            :key="label.id"
            class="inline-flex items-center gap-1 h-6 pl-1.5 pr-1 rounded-md border border-n-weak bg-n-alpha-2 text-mj-12 font-500 text-n-slate-12 max-w-full"
          >
            <span
              class="size-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="truncate max-w-[140px]">{{ label.title }}</span>
            <span
              role="button"
              tabindex="0"
              class="size-4 inline-flex items-center justify-center rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer flex-shrink-0"
              title="Remover etiqueta"
              @click="removeLabelFromConversation(label.title)"
              @keydown.enter.prevent="removeLabelFromConversation(label.title)"
            >
              <span class="i-lucide-x size-3" />
            </span>
          </span>

          <button
            type="button"
            class="h-6 inline-flex items-center gap-1 px-2 rounded-md border border-dashed border-n-weak text-mj-12 font-500 text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-mj-toque focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
            @click="toggleLabelDropdown"
          >
            <span class="i-lucide-plus size-3" />
            Adicionar
          </button>

          <div
            v-if="showLabelDropdown"
            class="absolute left-0 top-full mt-1 z-50 w-full min-w-[14rem] rounded-card border border-n-weak bg-n-background shadow-mj-4 p-2"
          >
            <LabelDropdown
              :account-labels="accountLabels"
              :selected-labels="savedLabels"
              :allow-creation="isAdmin"
              @add="addLabelToConversation"
              @remove="removeLabelFromConversation"
            />
          </div>
        </div>
      </div>

      <!-- ── DATAS — início + vencimento ── -->
      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-1.5">
          <span class="text-mj-12 font-500 text-n-slate-10"> Início </span>
          <input
            v-model="inicioEm"
            type="date"
            class="h-8 px-2 text-mj-12 text-n-slate-12 font-400 rounded-md border border-n-weak bg-n-background tabular-nums focus:outline-none focus:ring-1 focus:ring-n-slate-8 focus:border-transparent transition-colors duration-mj-toque"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="text-mj-12 font-500 text-n-slate-10"> Vencimento </span>
          <input
            v-model="venceEm"
            type="date"
            class="h-8 px-2 text-mj-12 text-n-slate-12 font-400 rounded-md border border-n-weak bg-n-background tabular-nums focus:outline-none focus:ring-1 focus:ring-n-slate-8 focus:border-transparent transition-colors duration-mj-toque"
          />
        </div>
      </div>

      <!-- ── TIMELINE — contexto secundário, colapsável (F2, read-only) ── -->
      <div class="flex flex-col gap-1.5 pt-1 border-t border-n-weak">
        <button
          type="button"
          class="flex items-center gap-1.5 -mx-1 px-1 py-0.5 rounded-md text-left hover:bg-n-alpha-1 transition-colors duration-mj-toque focus-visible:ring-2 focus-visible:ring-n-slate-12 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
          :aria-expanded="showTimeline"
          @click="toggleTimeline"
        >
          <span
            class="i-lucide-chevron-right size-3 text-n-slate-9 transition-transform duration-mj-toque flex-shrink-0"
            :class="{ 'rotate-90': showTimeline }"
          />
          <span class="text-mj-12 font-500 text-n-slate-10"> Timeline </span>
          <span
            v-if="dealEvents.length"
            class="text-mj-11 font-500 text-n-slate-10 tabular-nums"
          >
            {{ dealEvents.length }}
          </span>
        </button>
        <div v-if="showTimeline" class="pt-0.5">
          <CrmTimeline :events="dealEvents" />
        </div>
      </div>
    </div>
  </section>
</template>
